import React from 'react';
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Box, useTheme } from '@mui/material';
import { cubicTransition } from 'src/theme/transitions';
import { AnimationTiming } from 'recharts/types/util/types';
import { WidgetContent } from 'src/components/Widget/types';
import { DashboardDataExpenses } from 'src/api/services/types';
import {
  calculateEvenTicks,
  getFormattedXTick,
  getFormattedYTick,
} from '../../utils';

const CustomTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: any;
  label?: string;
}) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{ bgcolor: ({ palette }) => palette.paperGrey }}
        className="custom-tooltip"
      >
        <p className="label">{`${label} : ${payload[0].value}`}</p>
      </Box>
    );
  }

  return null;
};

const CustomTick = (props: any) => {
  const { index, x, y, axis, stroke, payload, tickFormatter } = props;
  const isXAxis = axis === 'x';
  const formattedValue = tickFormatter(payload.value, index);
  return (
    <g
      transform={`translate(${x + (isXAxis ? 0 : 12)},${y + (isXAxis ? 12 : 0)})`}
    >
      <text
        style={{ fontSize: 11, fontWeight: 600 }}
        x={0}
        y={0}
        textAnchor="start"
        fill="#000"
      >
        {formattedValue}
      </text>
    </g>
  );
};

const ExpensesGraphContent: WidgetContent<
  DashboardDataExpenses['dataPoints']
> = ({ data }) => {
  const theme = useTheme();
  const dataPoints = data ?? [];
  const maxPointValue = Math.max(...dataPoints.map((point) => point.y));

  return (
    <ResponsiveContainer width="100%" height="60%">
      <AreaChart
        data={dataPoints}
        style={{ direction: 'ltr' }}
        margin={{
          top: 0,
          right: 0,
          left: 0,
          bottom: 0,
        }}
      >
        <defs>
          <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1">
            <stop
              offset="0%"
              stopColor={theme.palette.primary.dark}
              stopOpacity={1}
            />
            <stop
              offset="30%"
              stopColor={theme.palette.primary.main}
              stopOpacity={0.75}
            />
            <stop
              offset="60%"
              stopColor={theme.palette.primary.light}
              stopOpacity={0.5}
            />
            <stop
              offset="100%"
              stopColor={theme.palette.primary.light}
              stopOpacity={0}
            />
          </linearGradient>
        </defs>
        <XAxis
          dataKey="x"
          minTickGap={0}
          tick={<CustomTick axis="x" />}
          axisLine={false}
          tickCount={dataPoints.length}
          tickLine={false}
          tickFormatter={getFormattedXTick}
          reversed
        />
        <YAxis
          orientation="right"
          axisLine={false}
          tick={<CustomTick axis="y" />}
          domain={[0, maxPointValue]}
          interval="preserveStartEnd"
          ticks={calculateEvenTicks(maxPointValue)}
          tickFormatter={getFormattedYTick}
          tickLine={false}
        />
        <Tooltip content={<CustomTooltip />} />
        <Area
          type="monotone"
          dataKey="y"
          stroke="transparent"
          fillOpacity={0.9}
          animationEasing={cubicTransition as AnimationTiming}
          animationDuration={750}
          // interval="preserveStartEnd"
          // animationEasing={'ease-in-out'} animationDuration={400}
          // animationEasing={cubicTransition as AnimationTiming} animationDuration={400}
          fill="url(#colorExpenses)"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default ExpensesGraphContent;
